import React from 'react';
import { CARD_INSTALLMENT_FEES, totalWithCardFee } from '../payments/cardInstallmentFees';
import './PaymentMethodsBlock.css';

const FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * @param {number} baseAmount — valor numérico da cobrança (mensalidade, anual ou produto)
 * @param {'plan-monthly'|'plan-annual'|'product'} variant
 * @param {string} [billingWord] — só para plan-annual (ex.: pagamento anual único)
 */
export default function PaymentMethodsBlock({ baseAmount, variant, billingWord }) {
  const pixTotal = FORMATTER.format(baseAmount);
  const cardRows =
    variant === 'plan-monthly'
      ? CARD_INSTALLMENT_FEES.filter((r) => r.installments === 1)
      : CARD_INSTALLMENT_FEES;

  const intro =
    variant === 'plan-annual' ? (
      <>
        Valores para esta cobrança ({billingWord}). No <strong>PIX</strong> não há custo adicional sobre o
        valor do plano. No <strong>cartão</strong>, você pode parcelar o pagamento anual em até 12× (taxas
        abaixo).
      </>
    ) : variant === 'plan-monthly' ? (
      <>
        Na <strong>mensalidade</strong>, cada cobrança pode ser por <strong>PIX</strong> (sem taxa adicional)
        ou <strong>cartão em 1×</strong> por mês. Parcelamento em várias vezes no cartão vale apenas para o{' '}
        <strong>plano anual</strong>.
      </>
    ) : (
      <>
        Valores para o <strong>valor listado do produto</strong> (frete à parte, se houver). No{' '}
        <strong>PIX</strong> não há custo adicional sobre esse valor. No <strong>cartão</strong>, você pode
        parcelar em até <strong>12×</strong> (taxas abaixo).
      </>
    );

  const cardNote =
    variant === 'plan-annual' || variant === 'product' ? (
      <>
        Quanto você paga em cada parcelamento (taxa já inclusa). Simulação — confirme na finalização.
      </>
    ) : (
      <>
        Somente <strong>1× à vista</strong> por mensalidade (taxa da operadora já inclusa). Simulação —
        confirme na finalização.
      </>
    );

  return (
    <details className="payment-methods-details">
      <summary className="payment-methods-summary">Ver formas de pagamento e valores</summary>
      <div className="payment-methods-body">
        <p className="payment-methods-intro">{intro}</p>

        <div className="payment-pix-box">
          <span className="payment-pix-title">PIX</span>
          <span className="payment-pix-value">{pixTotal}</span>
          <span className="payment-pix-note">Sem taxa adicional</span>
        </div>

        <h4 className="payment-card-heading">Cartão de crédito</h4>
        <p className="payment-card-note">{cardNote}</p>
        <div className="payment-table-wrap">
          <table className="payment-table">
            <thead>
              <tr>
                <th scope="col">Parcelamento</th>
                <th scope="col">Valor</th>
              </tr>
            </thead>
            <tbody>
              {cardRows.map((row) => {
                const total = totalWithCardFee(baseAmount, row.pct);
                const perInstallment = total / row.installments;
                const valorCell =
                  row.installments === 1
                    ? `${FORMATTER.format(total)} à vista`
                    : `${row.installments}× de ${FORMATTER.format(perInstallment)}`;
                return (
                  <tr key={row.installments}>
                    <td>{row.installments === 1 ? 'À vista (1×)' : `${row.installments}× no cartão`}</td>
                    <td className="payment-table-value">{valorCell}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}
